import { useMemo, useCallback } from "react"

import { merge } from "lodash"
import { useParams } from "react-router-dom"

import { filterFeaturesByRoad, sanitizeData } from "@l2r-front/l2r-geodata"
import {
    Layer,
    MapStyles,
    LINE_LAYER_SIZE,
    SELECTED_LINE_LAYER_SIZE,
    getLineLayerConfig,
    getLinearLocationsSelectableLineLayerWidth,
    getInterpolatedLineLayerWidth,
    getSelectableLineSortKey,
    REFERENTIAL_SOURCE,
    REFERENTIAL_LAYER,
    useOrderingLayers,
    useMapContext,
    SELECTABLE_REFERENTIAL_LAYER,
    REFERENTIAL_MAP_ID,
    Source,
} from "@l2r-front/l2r-map"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { useTheme } from "@l2r-front/l2r-ui"
import { selectLayerColorOpacity } from "@l2r-front/l2r-utils"

import { LINEAR_LOCATION_ROAD, LINEAR_LOCATION_SECTION } from "../../constants/linearLocation"
import { useNetworkReferential } from "../../hooks"

import { NetworkReferentialSource } from "../NetworkReferentialSource"

export function SelectableReferentialLayer(props) {

    const {
        mapId,
        roadToHide,
        onLinearLocationChanged,
        selectionType,
        selectedLinearLocation,
        ...layerProps
    } = props

    const { interactive = true } = layerProps
    const theme = useTheme()
    const [mapState] = useMapContext()
    const { currentMapStyle } = mapState
    const { road } = useParams()
    useOrderingLayers([SELECTABLE_REFERENTIAL_LAYER, REFERENTIAL_LAYER], mapId)

    const { data } = useNetworkReferential()

    // Need to copy id into properties because of that mapbox issue:
    // link:https://github.com/mapbox/mapbox-gl-js/issues/2716
    const sanitizedData = useMemo(() => {
        if (data) {
            const initialRoad = selectionType === LINEAR_LOCATION_SECTION && road ? road : null

            return initialRoad ? filterFeaturesByRoad(data, initialRoad) : sanitizeData(data)
        }
        return null
    }, [data, road, selectionType])

    const referentialLayerConfig = useMemo(() => {
        const initialRoad = selectionType === LINEAR_LOCATION_SECTION && road ? road : null
        const lineColor = currentMapStyle === MapStyles.plan ? selectLayerColorOpacity(theme.palette.map.darkReferential, 0.5, initialRoad) : selectLayerColorOpacity(theme.palette.map.lightReferential, 0.85, initialRoad)

        return merge(
            getLineLayerConfig({
                paint: {
                    "line-width": getInterpolatedLineLayerWidth(LINE_LAYER_SIZE),
                },
            }),
            {
                paint: {
                    "line-color": [
                        "case",
                        Number.isInteger(selectedLinearLocation?.start)
                            ? [
                                "all",
                                ["==", ["to-number", ["get", "start", ["at", 0, ["get", "linearLocation"]]]], selectedLinearLocation.start],
                                ["==", ["to-number", ["get", "end", ["at", 0, ["get", "linearLocation"]]]], selectedLinearLocation.end],
                                ["==", ["get", "road", ["at", 0, ["get", "linearLocation"]]], selectedLinearLocation.road],
                            ]
                            : [
                                "==",
                                ["get", "road", ["at", 0, ["get", "linearLocation"]]],
                                selectedLinearLocation?.road || null,
                            ],
                        theme.palette["colors/vert logiroad/400"].main,
                        lineColor,
                    ],
                    "line-width": getLinearLocationsSelectableLineLayerWidth(selectedLinearLocation),
                },
                layout: {
                    "line-sort-key": getSelectableLineSortKey(selectedLinearLocation?.road),
                },
                filter: ["!=", ["get", "road", ["at", 0, ["get", "linearLocation"]]], roadToHide || null],
            },
        )
    }, [currentMapStyle, road, selectedLinearLocation, theme.palette, roadToHide, selectionType])

    const referentialLayerClickConfig = useMemo(() => {
        return getLineLayerConfig({
            "paint": {
                "line-color": "transparent",
                "line-width": getInterpolatedLineLayerWidth(SELECTED_LINE_LAYER_SIZE),
            },
        })
    }, [])

    const handleLayerClick = useCallback((event) => {
        const feature = event?.feature
        if (!feature) {
            return
        } else {
            const linearLocation = JSON.parse(feature.properties.linearLocation)[0]
            if (selectionType === LINEAR_LOCATION_ROAD) {
                return onLinearLocationChanged({
                    road: linearLocation.road,
                    typeId: linearLocation.typeId,
                })
            } else if (selectionType === LINEAR_LOCATION_SECTION) {
                return onLinearLocationChanged(linearLocation)
            }
        }
    }, [selectionType, onLinearLocationChanged])

    return (
        <>
            <NetworkReferentialSource id={REFERENTIAL_SOURCE}>
                <Layer
                    {...referentialLayerConfig}
                    id={REFERENTIAL_LAYER}
                    {...layerProps}
                />
            </NetworkReferentialSource>
            {interactive && <Source type="geojson" data={sanitizedData} tolerance={.001} id="selectable-referential-layer-clickable">
                <Layer
                    {...referentialLayerClickConfig}
                    id={SELECTABLE_REFERENTIAL_LAYER}
                    mapId={mapId}
                    onClick={handleLayerClick}
                    {...layerProps}
                />
            </Source>
            }
        </>
    )
}

SelectableReferentialLayer.propTypes = {
    mapId: PropTypes.string,
    mapStyle: PropTypes.object,
    roadToHide: PropTypes.string,
    selectedRoad: PropTypes.string,
    selectedRoadColor: PropTypes.string,
    selectionType: PropTypes.oneOf([LINEAR_LOCATION_ROAD, LINEAR_LOCATION_SECTION]),
    onLinearLocationChanged: PropTypes.func.isRequired,
}

SelectableReferentialLayer.defaultProps = {
    mapId: REFERENTIAL_MAP_ID,
    selectionType: LINEAR_LOCATION_SECTION,
}
